import { Button } from "@/components/ui/Button"
import { Icon } from "@/components/icons/Icon"
import { useAtom, useAtomValue } from "jotai"
import { IconName } from "@/components/icons/icons"
import { CropPanel } from "@/components/Crop/CropPanel"
import { Fragment } from "react"
import { useTranslation } from "react-i18next"
import { activeEditingToolPanelAtom } from "@/store/ui-views-state"
import { ToolPanel } from "@/store/undoable-state"
import { Adjustments } from "@/core/types"
import { MaskAdjustmentsPanel } from "@/components/Masking/MaskAdjustmentsPanel"
import { activeAdjustmentsAtom } from "@/store/store-history"
import { HealingPanel } from "@/components/Healing/HealingPanel"
import { useAreMasksSupported, useIsHealingSupported } from "@/store/app/browser-capabilities"
import { pushDialog } from "@/store/dialogs-state"
import { MasksUnavailableDialog } from "@/components/Dialogs/MasksUnavailableDialog"
import { HealingUnavailableDialog } from "@/components/Dialogs/HealingUnavailableDialog"
import { PeoplePanel } from "@/components/People/PeoplePanel"
import { isRetouchingEnabled } from "@/utils/measurement/feature-flag"

export interface ToolPanelProps {
	disabled?: boolean
	adjustments: Adjustments
}

export const ControlToolsPanel = (props: ToolPanelProps) => {
	const [activePanel] = useAtom(activeEditingToolPanelAtom)
	const peopleRetouchingEnabled = isRetouchingEnabled()

	switch (activePanel) {
		case "crop":
			return <CropPanel {...props} />

		case "masking":
			return <MaskAdjustmentsPanel />

		case "healing":
			return <HealingPanel />

		case "people":
			return peopleRetouchingEnabled ? <PeoplePanel /> : <Fragment />

		default:
			return <Fragment />
	}
}

export const ControlsToolBar = () => {
	const activeAdjustments = useAtomValue(activeAdjustmentsAtom)
	const areMasksSupported = useAreMasksSupported()
	const isHealingSupported = useIsHealingSupported()
	const peopleRetouchingEnabled = isRetouchingEnabled()
	const { t } = useTranslation("components")

	return (
		<div>
			<div className="sticky top-0 z-10 flex justify-center bg-base-800 p-2">
				<ToolbarButton
					icon="adjust"
					panel="edit"
					active={activeAdjustments.globalAdjustments}
					tooltip={t("controlsToolBar.tooltipEdit")}
				/>
				<ToolbarButton
					icon="crop"
					panel="crop"
					active={activeAdjustments.crop}
					tooltip={t("controlsToolBar.tooltipCrop")}
				/>

				<ToolbarButton
					icon="brush_erase_mask"
					panel="healing"
					active={activeAdjustments.healing}
					tooltip={t("controlsToolBar.tooltipRemove")}
					onDisabledClick={
						!isHealingSupported ? () => pushDialog(HealingUnavailableDialog) : undefined
					}
				/>
				<ToolbarButton
					icon="masking"
					panel="masking"
					active={activeAdjustments.masking}
					tooltip={t("controlsToolBar.tooltipMasking")}
					onDisabledClick={
						!areMasksSupported ? () => pushDialog(MasksUnavailableDialog) : undefined
					}
				/>
				{peopleRetouchingEnabled && (
					<ToolbarButton
						icon="faces"
						panel="people"
						active={activeAdjustments.masking}
						tooltip={t("controlsToolBar.people")}
						onDisabledClick={
							!areMasksSupported
								? () => pushDialog(MasksUnavailableDialog)
								: undefined
						}
					/>
				)}
			</div>
		</div>
	)
}

const ToolbarButton = (props: {
	icon: IconName
	panel: ToolPanel
	active?: boolean
	tooltip?: string
	onDisabledClick?: () => void
}) => {
	const [activePanel, setActivePanel] = useAtom(activeEditingToolPanelAtom)

	const handleClick = () => {
		if (props.onDisabledClick) {
			props.onDisabledClick()
			return
		}
		if (activePanel === props.panel) {
			setActivePanel("edit")
		} else {
			setActivePanel(props.panel)
		}
	}

	return (
		<Button
			trackingLabel={`open_${props.panel}`}
			variant="icon"
			className="relative px-6"
			isSelected={!props.onDisabledClick && activePanel === props.panel}
			onClick={handleClick}
			disableEnterKey
			tooltip={props.tooltip}
		>
			<Icon name={props.icon} />
			{props.active && (
				<div className="absolute right-[3px] top-[3px] size-[3px] rounded-full bg-current" />
			)}
		</Button>
	)
}
